import { LOCALES, LOCALE_NAMES, LOCALE_TAGS, type Locale } from '../../src/i18n/index';
import { T, TN, api, errorText, esc, getLocale, type ApiError } from '../shared/client';
import { LOOK_PRESETS, lookOf } from '../../src/domain/templates';
import { HOLIDAY_COUNTRIES, holidaysFor, type HolidayCountry } from '../../src/domain/holidays';
import { LayoutEditor } from './floorplan';

/**
 * Configuration screen: shifts, tables, combinations, policies, closures,
 * booking settings, languages, branding and email wording, edited as a draft
 * and published as a unit. The server validates and enforces everything; this
 * screen only makes it editable.
 */

interface Opts {
  restaurantId: string;
  canPublish: boolean;
  toast: (msg: string, isErr?: boolean) => void;
  /** Called after a publish so the host stand reloads its state. */
  onPublished: () => void;
  onClose: () => void;
  /** Open on this section instead of the first. */
  section?: string;
}

type Section = 'shifts' | 'schedule' | 'experiences' | 'areas' | 'tables' | 'layout' | 'policies' | 'closures' | 'settings' | 'branding' | 'widget' | 'history';
const SECTIONS: Section[] = ['shifts', 'schedule', 'experiences', 'areas', 'tables', 'layout', 'policies', 'closures', 'settings', 'branding', 'widget', 'history'];
type EmailKind = 'confirmation' | 'reminder' | 'change' | 'cancellation' | 'review';
const EMAIL_KINDS: EmailKind[] = ['confirmation', 'reminder', 'change', 'cancellation', 'review'];

const uid = () => (crypto as any).randomUUID() as string;
const TIMES = (() => {
  const a: string[] = [];
  for (let m = 0; m < 24 * 60; m += 15) a.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  return a;
})();
const money = (minor: number, cur = 'USD') =>
  new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: cur }).format(minor / 100);

/** Weekday names, Monday first, in the current language. */
function dayNames(): string[] {
  const fmt = new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(Date.UTC(2024, 0, 1 + i))));
}

export function openConfigScreen(opts: Opts): void {
  const root = document.createElement('div');
  root.className = 'cfg-root';
  document.body.appendChild(root);
  document.body.classList.add('cfg-open');

  let state: any = null; // GET /config
  let doc: any = null; // working copy of the draft
  let draft: any = null; // server view of the draft (diff, errors, warnings)
  let section: Section = (SECTIONS as string[]).includes(opts.section ?? '') ? (opts.section as Section) : 'shifts';
  let saving = false;
  let saveTimer = 0 as any;
  let previewTimer = 0 as any;
  let note = '';
  let busy = false;
  let emailKind: EmailKind = 'confirmation';
  let emailLocale: Locale = getLocale();
  let previewFee = true;
  let widgetLocale: Locale = getLocale();
  let experiences: any[] | null = null;
  let selShift = '';
  let holidayCountry: HolidayCountry = 'DE';
  let holidayYear = new Date().getFullYear();
  let showHolidays = false;
  let showRange = false;
  let embedLang = '';
  let embedHeight = 640;
  let embedParty = 2;
  let widgetTimer = 0 as any;
  const LOGO_MAX = 512 * 1024;
  let previewPolicy = true;
  const layout = new LayoutEditor({
    doc: () => doc,
    changed: () => scheduleSave(),
    toast: opts.toast,
  });

  const err = (e: unknown) => opts.toast(errorText(e), true);
  const base = `/v1/restaurants/${opts.restaurantId}/config`;

  async function refresh(keepDoc = false) {
    state = await api('GET', base);
    draft = state.draft;
    if (!keepDoc) doc = draft ? structuredClone(draft.doc) : null;
    render();
  }

  // ------------------------------------------------------------ draft lifecycle
  async function startDraft(replace = false) {
    try {
      draft = await api('POST', `${base}/draft`, { replace });
      doc = structuredClone(draft.doc);
      state.draft = draft;
      render();
    } catch (e) {
      err(e);
    }
  }

  function scheduleSave() {
    if (section === 'widget') schedulePush();
    saving = true;
    paintBar();
    clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 600);
  }

  async function save() {
    if (!doc) return;
    try {
      draft = await api('PUT', `${base}/draft`, doc);
      state.draft = draft;
    } catch (e) {
      const ex = e as ApiError;
      if (ex.status === 422 && ex.details?.issues?.some((i: any) => String(i.path).includes('customCss')))
        opts.toast(T('c.w.css_bad'), true);
      else if (ex.status === 422 && ex.details?.issues)
        opts.toast(ex.details.issues.map((i: any) => `${i.path}: ${i.message}`).slice(0, 2).join(' · '), true);
      else err(e);
    } finally {
      saving = false;
      paintBar();
    }
  }

  async function discard() {
    if (!confirm(T('c.discard_confirm'))) return;
    try {
      await api('DELETE', `${base}/draft`);
      await refresh();
    } catch (e) {
      err(e);
    }
  }

  async function publish() {
    if (busy) return;
    busy = true;
    paintBar();
    try {
      clearTimeout(saveTimer);
      await save();
      const r = await api('POST', `${base}/publish`, { note: note || undefined });
      opts.toast(T('c.published', { v: r.version }));
      note = '';
      await refresh();
      opts.onPublished();
    } catch (e) {
      const ex = e as ApiError;
      if (ex.code === 'config_stale') opts.toast(T('err.config_stale'), true);
      else err(e);
      await refresh(true).catch(() => undefined);
    } finally {
      busy = false;
      paintBar();
    }
  }

  // ------------------------------------------------------------ helpers
  /** Set a value by dotted path, creating missing objects on the way. */
  const setPath = (path: string, value: unknown) => {
    const parts = path.split('.');
    let o = doc;
    for (let i = 0; i < parts.length - 1; i += 1) {
      if (o[parts[i]] == null || typeof o[parts[i]] !== 'object') o[parts[i]] = {};
      o = o[parts[i]];
    }
    const last = parts[parts.length - 1];
    if (value === undefined) delete o[last];
    else o[last] = value;
  };
  const getPath = (path: string) => path.split('.').reduce((o, k) => o?.[k], doc);

  const input = (path: string, type: 'text' | 'opt' | 'nullable' | 'int' | 'money' | 'color', attrs = '') => {
    let v = getPath(path);
    if (type === 'money') v = v == null ? '' : (v / 100).toFixed(2);
    const html = type === 'int' || type === 'money' ? 'number' : type === 'color' ? 'color' : 'text';
    const extra = type === 'money' ? 'step="0.01" min="0"' : type === 'int' ? 'step="1"' : '';
    return `<input type="${html}" data-path="${path}" data-type="${type}" value="${esc(v ?? '')}" ${extra} ${attrs}>`;
  };
  const timeSelect = (path: string) =>
    `<select data-path="${path}" data-type="text">${TIMES.map((t) => `<option ${t === getPath(path) ? 'selected' : ''}>${t}</option>`).join('')}</select>`;
  const check = (path: string, label: string) =>
    `<label class="cfg-check"><input type="checkbox" data-path="${path}" data-type="bool" ${getPath(path) ? 'checked' : ''}> ${esc(label)}</label>`;

  // ------------------------------------------------------------ sections
  function shiftsSection(): string {
    const days = dayNames();
    const order = doc.shifts
      .map((s: any, i: number) => ({ s, i }))
      .sort((a: any, b: any) => a.s.first.localeCompare(b.s.first) || a.s.name.localeCompare(b.s.name));
    const cards = order
      .map(({ s, i }: { s: any; i: number }) => {
        const p = `shifts.${i}`;
        const dayBoxes = days
          .map(
            (d, k) =>
              `<label class="cfg-check"><input type="checkbox" data-set="shift-day" data-i="${i}" data-day="${k + 1}" ${s.days.includes(k + 1) ? 'checked' : ''}> ${esc(d)}</label>`,
          )
          .join('');
        const plans = doc.floorPlans
          .map(
            (f: any) =>
              `<label class="cfg-check"><input type="checkbox" data-set="shift-plan" data-i="${i}" data-id="${f.id}" ${s.floorPlanIds.includes(f.id) ? 'checked' : ''}> ${esc(f.name)}</label>`,
          )
          .join('');
        const turns = s.turnRules
          .map(
            (_: any, k: number) => `<tr>
              <td>${input(`${p}.turnRules.${k}.min`, 'int', 'min="1"')}</td>
              <td>${input(`${p}.turnRules.${k}.max`, 'int', 'min="1"')}</td>
              <td>${input(`${p}.turnRules.${k}.minutes`, 'int', 'min="5"')}</td>
              <td><button class="ghost" data-do="del-turn" data-i="${i}" data-k="${k}" aria-label="${esc(T('c.s.remove_aria'))}">×</button></td></tr>`,
          )
          .join('');
        const excluded = doc.tables
          .map((t: any) => {
            const pol = s.tablePolicy.find((x: any) => x.tableId === t.id);
            const takes = pol ? pol.takesReservations : true;
            return `<label class="cfg-check"><input type="checkbox" data-set="shift-table" data-i="${i}" data-id="${t.id}" ${takes ? 'checked' : ''}> ${esc(t.label)}</label>`;
          })
          .join('');
        return `<section class="cfg-card">
          <div class="cfg-row">
            <label>${esc(T('c.s.name'))} ${input(`${p}.name`, 'text', 'maxlength="80"')}</label>
            <label>${esc(T('c.s.colour'))} ${input(`${p}.color`, 'color')}</label>
            <label>${esc(T('c.s.first'))} ${timeSelect(`${p}.first`)}</label>
            <label>${esc(T('c.s.last'))} ${timeSelect(`${p}.last`)}</label>
            <label>${esc(T('c.s.max_online'))} ${input(`${p}.maxOnlineParty`, 'int', `min="1" placeholder="${esc(T('c.s.default'))}"`)}</label>
            <button class="danger" data-do="del-shift" data-i="${i}">${esc(T('c.s.remove'))}</button>
          </div>
          <div class="cfg-row"><span class="cfg-label">${esc(T('c.s.days'))}</span>${dayBoxes}</div>
          <div class="cfg-row"><span class="cfg-label">${esc(T('c.s.plans'))}</span>${plans || `<span class="muted small">${esc(T('common.none'))}</span>`}</div>
          <div class="cfg-row">
            <span class="cfg-label">${esc(T('c.s.flow'))}</span>
            <label class="cfg-check"><input type="checkbox" data-set="flow-on" data-i="${i}" ${s.flow ? 'checked' : ''}> ${esc(T('c.s.limit_arrivals'))}</label>
            ${
              s.flow
                ? `<label>${esc(T('c.s.per'))} <select data-path="${p}.flow.bucket" data-type="int">${[10, 15, 30, 60].map((m) => `<option value="${m}" ${s.flow.bucket === m ? 'selected' : ''}>${esc(T('c.s.min', { n: m }))}</option>`).join('')}</select></label>
                   <label>${esc(T('c.s.max_covers'))} ${input(`${p}.flow.maxCovers`, 'int', 'min="1"')}</label>
                   <label>${esc(T('c.s.max_parties'))} ${input(`${p}.flow.maxParties`, 'int', `min="1" placeholder="${esc(T('c.s.none_ph'))}"`)}</label>`
                : ''
            }
          </div>
          <details><summary>${esc(T('c.s.turn_times'))} <span class="muted small">${esc(TN('c.s.rules', s.turnRules.length))}</span></summary>
            <table class="cfg-table"><thead><tr><th>${esc(T('c.s.party_from'))}</th><th>${esc(T('c.s.to'))}</th><th>${esc(T('c.s.minutes'))}</th><th></th></tr></thead><tbody>${turns}</tbody></table>
            <button data-do="add-turn" data-i="${i}">${esc(T('c.s.add_turn'))}</button>
          </details>
          <details><summary>${esc(T('c.s.take_res'))} <span class="muted small">${esc(T('c.s.take_res_hint'))}</span></summary>
            <div class="cfg-wrap">${excluded}</div>
          </details>
        </section>`;
      })
      .join('');
    return `${cards || `<p class="muted">${esc(T('c.s.none'))}</p>`}<button class="primary" data-do="add-shift">${esc(T('c.s.add'))}</button>`;
  }

  // ------------------------------------------------------------ weekly schedule grid
  const GRID_FROM = 6 * 60;
  const GRID_TO = 24 * 60;
  const PX_PER_MIN = 0.55;
  const toMin = (v: string) => Number(String(v).slice(0, 2)) * 60 + Number(String(v).slice(3, 5));
  const fromMin = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

  function scheduleSection(): string {
    const shifts: any[] = doc.shifts.map((x: any, i: number) => ({ x, i })).filter((e: any) => !e.x.specialDayId);
    const names = dayNames();
    const hours: string[] = [];
    for (let m = GRID_FROM; m < GRID_TO; m += 60) hours.push(`<span style="top:${(m - GRID_FROM) * PX_PER_MIN}px">${fromMin(m)}</span>`);
    const cols = [1, 2, 3, 4, 5, 6, 7]
      .map((d) => {
        const blocks = shifts
          .filter((e) => e.x.days.includes(d))
          .map((e) => {
            const a = Math.max(GRID_FROM, toMin(e.x.first));
            const b = Math.min(GRID_TO, toMin(e.x.last) + 30);
            return `<div class="sg-block ${selShift === e.x.id ? 'sel' : ''}" data-shift="${e.x.id}" data-i="${e.i}"
              style="top:${(a - GRID_FROM) * PX_PER_MIN}px;height:${Math.max(18, (b - a) * PX_PER_MIN)}px;background:${esc(e.x.color)}" title="${esc(`${e.x.name} ${e.x.first}–${e.x.last}`)}">
              <strong>${esc(e.x.name)}</strong><span>${esc(`${e.x.first}–${e.x.last}`)}</span><i class="sg-grip" data-grip="${e.i}"></i></div>`;
          })
          .join('');
        return `<div class="sg-col"><div class="sg-head">${esc(names[d - 1])}</div><div class="sg-track" style="height:${(GRID_TO - GRID_FROM) * PX_PER_MIN}px">${blocks}</div></div>`;
      })
      .join('');
    const cur = shifts.find((e) => e.x.id === selShift);
    const panel = cur
      ? `<div class="cfg-card"><h3 style="margin-top:0">${esc(cur.x.name)}</h3>
          <div class="cfg-row">
            <label>${esc(T('c.s.name'))} ${input(`shifts.${cur.i}.name`, 'text', 'maxlength="60"')}</label>
            <label>${esc(T('c.s.colour'))} ${input(`shifts.${cur.i}.color`, 'color')}</label>
            <label>${esc(T('c.s.first'))} ${timeSelect(`shifts.${cur.i}.first`)}</label>
            <label>${esc(T('c.s.last'))} ${timeSelect(`shifts.${cur.i}.last`)}</label>
          </div>
          <div class="cfg-row">${dayNames().map((n, di) => `<label class="cfg-check"><input type="checkbox" data-set="shift-day" data-i="${cur.i}" data-day="${di + 1}" ${cur.x.days.includes(di + 1) ? 'checked' : ''}> ${esc(n)}</label>`).join('')}</div>
          <p class="muted small">${esc(T('c.sg.hint_edit'))}</p></div>`
      : `<p class="muted small">${esc(T('c.sg.hint'))}</p>`;
    return `<p class="muted small">${esc(T('c.sg.intro'))}</p>
      <div class="sg"><div class="sg-hours" style="margin-top:28px">${hours.join('')}</div>${cols}</div>
      ${panel}
      <div class="cfg-row"><button data-do="add-shift-grid">${esc(T('c.sg.add'))}</button></div>`;
  }

  /** Drag a block up or down to move a shift in time, or its bottom edge to change when the last seating is. */
  let sgDrag: null | { i: number; mode: 'move' | 'resize'; y: number; first: number; last: number; moved: boolean } = null;
  root.addEventListener('pointerdown', (ev) => {
    const g = (ev.target as HTMLElement).closest('[data-grip]') as HTMLElement | null;
    const b = (ev.target as HTMLElement).closest('.sg-block') as HTMLElement | null;
    if (!b || !doc) return;
    const i = Number(b.dataset.i);
    const sh = doc.shifts[i];
    sgDrag = { i, mode: g ? 'resize' : 'move', y: ev.clientY, first: toMin(sh.first), last: toMin(sh.last), moved: false };
    selShift = sh.id;
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    ev.preventDefault();
  });
  root.addEventListener('pointermove', (ev) => {
    if (!sgDrag || !doc) return;
    const dy = ev.clientY - sgDrag.y;
    if (!sgDrag.moved && Math.abs(dy) < 3) return;
    sgDrag.moved = true;
    const snapped = Math.round(dy / PX_PER_MIN / 15) * 15;
    const sh = doc.shifts[sgDrag.i];
    if (sgDrag.mode === 'move') {
      const len = sgDrag.last - sgDrag.first;
      const first = Math.max(0, Math.min(24 * 60 - 15 - len, sgDrag.first + snapped));
      sh.first = fromMin(first);
      sh.last = fromMin(first + len);
    } else {
      sh.last = fromMin(Math.max(sgDrag.first + 15, Math.min(24 * 60 - 15, sgDrag.last + snapped)));
    }
    // Repaint just the blocks of this shift while dragging.
    root.querySelectorAll(`.sg-block[data-shift="${sh.id}"]`).forEach((el) => {
      const a = Math.max(GRID_FROM, toMin(sh.first));
      const e = Math.min(GRID_TO, toMin(sh.last) + 30);
      (el as HTMLElement).style.top = `${(a - GRID_FROM) * PX_PER_MIN}px`;
      (el as HTMLElement).style.height = `${Math.max(18, (e - a) * PX_PER_MIN)}px`;
      const span = el.querySelector('span');
      if (span) span.textContent = `${sh.first}–${sh.last}`;
    });
  });
  const endGridDrag = () => {
    if (!sgDrag) return;
    const moved = sgDrag.moved;
    sgDrag = null;
    if (moved) scheduleSave();
    render();
  };
  root.addEventListener('pointerup', endGridDrag);
  root.addEventListener('pointercancel', endGridDrag);

  // ------------------------------------------------------------ experiences
  async function loadExperiences() {
    try {
      experiences = (await api<any>('GET', `/v1/restaurants/${opts.restaurantId}/experiences`)).experiences;
    } catch (e) {
      err(e);
      experiences = [];
    }
    render();
  }

  function experiencesSection(): string {
    if (experiences == null) {
      void loadExperiences();
      return `<p class="muted">${esc(T('c.loading'))}</p>`;
    }
    const cards = experiences
      .map((x: any, i: number) => {
        const f = (name: string) => `data-x="${i}" data-xf="${name}"`;
        return `<div class="cfg-card" data-xcard="${i}">
          ${LOCALES.map((l) => `<div class="cfg-row">
            <label style="flex:1 1 220px">${esc(T('c.x.title', { language: LOCALE_NAMES[l] }))}<input ${f(`titles.${l}`)} maxlength="80" value="${esc(x.titles?.[l] ?? '')}"></label>
            <label style="flex:2 1 320px">${esc(T('c.x.desc', { language: LOCALE_NAMES[l] }))}<input ${f(`descriptions.${l}`)} maxlength="600" value="${esc(x.descriptions?.[l] ?? '')}"></label></div>`).join('')}
          <div class="cfg-row">
            <label>${esc(T('c.x.price'))}<input type="number" step="0.01" min="0" ${f('price')} value="${(x.priceMinor / 100).toFixed(2)}"></label>
            <label>${esc(T('c.x.start'))}<select ${f('startTime')}>${TIMES.map((t) => `<option ${t === x.startTime ? 'selected' : ''}>${t}</option>`).join('')}</select></label>
            <label>${esc(T('c.x.end'))}<select ${f('endTime')}>${TIMES.map((t) => `<option ${t === x.endTime ? 'selected' : ''}>${t}</option>`).join('')}</select></label>
            <label>${esc(T('c.x.min_party'))}<input type="number" min="1" ${f('minParty')} value="${x.minParty}"></label>
            <label>${esc(T('c.x.max_party'))}<input type="number" min="1" ${f('maxParty')} value="${x.maxParty}"></label>
            <label>${esc(T('c.x.max_covers'))}<input type="number" min="1" ${f('maxCovers')} value="${x.maxCovers ?? ''}" placeholder="${esc(T('c.x.unlimited'))}"></label>
          </div>
          <div class="cfg-row">
            <label>${esc(T('c.x.from'))}<input type="date" ${f('dateFrom')} value="${esc(x.dateFrom ?? '')}"></label>
            <label>${esc(T('c.x.to'))}<input type="date" ${f('dateTo')} value="${esc(x.dateTo ?? '')}"></label>
            ${dayNames().map((n, di) => `<label class="cfg-check"><input type="checkbox" data-x="${i}" data-xday="${di + 1}" ${x.days.includes(di + 1) ? 'checked' : ''}> ${esc(n)}</label>`).join('')}
            <label class="cfg-check"><input type="checkbox" ${f('active')} ${x.active ? 'checked' : ''}> ${esc(T('c.x.active'))}</label>
          </div>
          <div class="cfg-row">
            <label style="flex:1 1 260px">${esc(T('x.access.code'))}<input ${f('accessCode')} maxlength="40" value="${esc(x.accessCode ?? '')}"></label>
            <label style="flex:1 1 260px">${esc(T('x.access.tags'))}<input ${f('accessTags')} maxlength="200" value="${esc((x.accessTags ?? []).join(', '))}"></label>
          </div>
          <div class="cfg-row"><button class="primary" data-do="x-save" data-i="${i}">${esc(T('common.save'))}</button>
            <button class="danger" data-do="x-del" data-i="${i}">${esc(T('g.delete'))}</button></div></div>`;
      })
      .join('');
    return `<div class="cfg-card"><p class="muted small">${esc(T('c.x.intro'))}</p>
      <button class="primary" data-do="x-add">${esc(T('c.x.add'))}</button></div>
      ${cards || `<p class="muted">${esc(T('c.x.none'))}</p>`}`;
  }

  /** Read the values of one experience card back into the object. */
  function readExperience(i: number) {
    const card = root.querySelector(`[data-xcard="${i}"]`) as HTMLElement;
    const val = (n: string) => (card.querySelector(`[data-xf="${n}"]`) as HTMLInputElement).value.trim();
    const titles: Record<string, string> = {};
    const descriptions: Record<string, string> = {};
    for (const l of LOCALES) {
      titles[l] = val(`titles.${l}`);
      descriptions[l] = val(`descriptions.${l}`);
    }
    return {
      titles, descriptions,
      priceMinor: Math.round(Number(val('price') || 0) * 100),
      days: [...card.querySelectorAll<HTMLInputElement>('[data-xday]')].filter((c) => c.checked).map((c) => Number(c.dataset.xday)),
      dateFrom: val('dateFrom') || null, dateTo: val('dateTo') || null,
      startTime: val('startTime'), endTime: val('endTime'),
      minParty: Number(val('minParty') || 1), maxParty: Number(val('maxParty') || 1),
      maxCovers: val('maxCovers') ? Number(val('maxCovers')) : null,
      active: (card.querySelector('[data-xf="active"]') as HTMLInputElement).checked,
      accessCode: val('accessCode') || null,
      accessTags: val('accessTags').split(',').map((v) => v.trim()).filter(Boolean).slice(0, 10),
    };
  }

  // ------------------------------------------------------------ special days: range, holidays, copy
  function specialTools(): string {
    const hs = holidaysFor(holidayCountry, holidayYear);
    const have = new Set<string>(doc.specialDays.map((d: any) => d.day));
    const lang = getLocale();
    return `<div class="cfg-card">
      <div class="cfg-row">
        <button data-do="toggle-range">${esc(T('c.cl.range'))}</button>
        <button data-do="toggle-holidays">${esc(T('c.cl.holidays'))}</button>
      </div>
      ${showRange ? `<div class="cfg-row">
          <label>${esc(T('c.cl.from'))}<input type="date" id="rg-from"></label>
          <label>${esc(T('c.cl.to'))}<input type="date" id="rg-to"></label>
          <label>${esc(T('c.cl.title'))}<input id="rg-title" maxlength="80" placeholder="${esc(T('c.cl.range_ph'))}"></label>
          <button class="primary" data-do="range-apply">${esc(T('c.cl.range_apply'))}</button></div>` : ''}
      ${showHolidays ? `<div class="cfg-row">
          <label>${esc(T('c.cl.country'))}<select data-ui="hol-country">${HOLIDAY_COUNTRIES.map((c) => `<option ${c === holidayCountry ? 'selected' : ''}>${c}</option>`).join('')}</select></label>
          <label>${esc(T('c.cl.year'))}<input type="number" data-ui="hol-year" value="${holidayYear}" min="2020" max="2100" style="width:100px"></label></div>
        <div class="hol-list">${hs.map((h) => `<label class="cfg-check"><input type="checkbox" data-hol="${h.day}" data-name="${esc(h.name[lang === 'de' ? 'de' : 'en'])}" ${have.has(h.day) ? 'disabled' : 'checked'}> ${esc(h.day)} · ${esc(h.name[lang === 'de' ? 'de' : 'en'])}${have.has(h.day) ? ` <span class="muted small">${esc(T('c.cl.have'))}</span>` : ''}</label>`).join('')}</div>
        <div class="cfg-row"><button class="primary" data-do="hol-apply">${esc(T('c.cl.hol_apply'))}</button></div>` : ''}
      <p class="muted small">${esc(T('c.cl.tools_hint'))}</p></div>`;
  }

  // ------------------------------------------------------------ areas
  const AREA_KINDS = ['indoor', 'private', 'smoking', 'outdoor', 'patio', 'bar', 'lounge', 'other'] as const;

  function areasSection(): string {
    const suggestions = (['private', 'smoking', 'outdoor', 'bar', 'lounge'] as const)
      .map((k) => `<button data-do="add-area" data-kind="${k}">+ ${esc(T(`env.${k}`))}</button>`)
      .join('');
    const cards = doc.areas
      .map((a: any, i: number) => {
        const p = `areas.${i}`;
        const count = doc.tables.filter((t: any) => t.diningAreaId === a.id).length;
        const kinds = AREA_KINDS.map((k) => `<option value="${k}" ${a.environment === k ? 'selected' : ''}>${esc(T(`env.${k}`))}</option>`).join('');
        const perLang = LOCALES.map(
          (l) => `<div class="cfg-row">
            <label style="flex:1 1 200px">${esc(T('c.a.name_in', { language: LOCALE_NAMES[l] }))} ${input(`${p}.names.${l}`, 'opt', 'maxlength="60"')}</label>
            <label style="flex:2 1 300px">${esc(T('c.a.desc_in', { language: LOCALE_NAMES[l] }))} ${input(`${p}.descriptions.${l}`, 'opt', 'maxlength="200"')}</label></div>`,
        ).join('');
        return `<div class="cfg-card">
          <div class="cfg-row">
            <label style="flex:1 1 220px">${esc(T('c.a.name'))} ${input(`${p}.name`, 'text', 'maxlength="60" required')}</label>
            <label>${esc(T('c.a.kind'))} <select data-path="${p}.environment" data-type="text">${kinds}</select></label>
            ${check(`${p}.bookable`, T('c.a.bookable'))}
          </div>
          <details><summary>${esc(T('c.a.texts'))}</summary>${perLang}<p class="muted small">${esc(T('c.a.texts_hint'))}</p></details>
          <div class="cfg-row">
            <span class="muted small">${esc(TN('c.a.tables', count))}</span>
            <button data-do="goto-layout">${esc(T('c.a.place'))}</button>
            <button class="danger" data-do="del-area" data-i="${i}">${esc(T('c.a.delete'))}</button>
          </div>
        </div>`;
      })
      .join('');
    return `<div class="cfg-card"><p class="muted small">${esc(T('c.a.intro'))}</p>
      <div class="cfg-wrap" style="gap:6px"><span class="cfg-label">${esc(T('c.a.quick'))}</span>${suggestions}
      <button class="primary" data-do="add-area" data-kind="indoor">${esc(T('c.a.add'))}</button></div></div>
      ${cards || `<p class="muted">${esc(T('c.a.none'))}</p>`}`;
  }

  function tablesSection(): string {
    const areaOpts = (sel: string | null) =>
      `<option value="">—</option>` +
      doc.areas.map((a: any) => `<option value="${a.id}" ${a.id === sel ? 'selected' : ''}>${esc(a.name)}</option>`).join('');
    const planOpts = (sel: string) =>
      doc.floorPlans.map((f: any) => `<option value="${f.id}" ${f.id === sel ? 'selected' : ''}>${esc(f.name)}</option>`).join('');
    const nat = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true });
    const rows = doc.tables
      .map((t: any, i: number) => ({ t, i }))
      .sort((a: any, b: any) => nat(a.t.label, b.t.label))
      .map(
        ({ t, i }: { t: any; i: number }) => `<tr>
          <td>${input(`tables.${i}.label`, 'text', 'maxlength="20" style="width:70px"')}</td>
          <td>${input(`tables.${i}.minSeats`, 'int', 'min="1" style="width:60px"')}</td>
          <td>${input(`tables.${i}.maxSeats`, 'int', 'min="1" style="width:60px"')}</td>
          <td><select data-path="tables.${i}.diningAreaId" data-type="nullable">${areaOpts(t.diningAreaId)}</select></td>
          <td><select data-path="tables.${i}.floorPlanId" data-type="text">${planOpts(t.floorPlanId)}</select></td>
          <td><select data-path="tables.${i}.shape" data-type="text">${['rect', 'round', 'diamond'].map((x) => `<option value="${x}" ${t.shape === x ? 'selected' : ''}>${esc(T(`c.t.shape.${x}`))}</option>`).join('')}</select></td>
          <td><button class="ghost" data-do="del-table" data-i="${i}" aria-label="${esc(T('c.t.remove_aria', { label: t.label }))}">×</button></td></tr>`,
      )
      .join('');
    const combos = doc.combos
      .map(
        (c: any, i: number) => `<div class="cfg-card">
          <div class="cfg-row">
            <label>${esc(T('c.s.party_from'))} ${input(`combos.${i}.minParty`, 'int', 'min="1" style="width:70px"')}</label>
            <label>${esc(T('c.s.to'))} ${input(`combos.${i}.maxParty`, 'int', 'min="1" style="width:70px"')}</label>
            <button class="danger" data-do="del-combo" data-i="${i}">${esc(T('common.remove'))}</button>
          </div>
          <div class="cfg-wrap">${doc.tables
            .map(
              (t: any) =>
                `<label class="cfg-check"><input type="checkbox" data-set="combo-table" data-i="${i}" data-id="${t.id}" ${c.tableIds.includes(t.id) ? 'checked' : ''}> ${esc(t.label)}</label>`,
            )
            .join('')}</div>
        </div>`,
      )
      .join('');
    return `
      <h3>${esc(T('c.sec.tables'))}</h3>
      <table class="cfg-table"><thead><tr><th>${esc(T('c.t.label'))}</th><th>${esc(T('c.t.min'))}</th><th>${esc(T('c.t.max'))}</th><th>${esc(T('c.t.area'))}</th><th>${esc(T('c.t.plan'))}</th><th>${esc(T('c.t.shape'))}</th><th></th></tr></thead>
      <tbody>${rows}</tbody></table>
      <button class="primary" data-do="add-table">${esc(T('c.t.add'))}</button>
      <h3>${esc(T('c.t.combos'))}</h3>
      <p class="muted small">${esc(T('c.t.combos_hint'))}</p>
      ${combos || `<p class="muted small">${esc(T('c.t.combos_none'))}</p>`}
      <button data-do="add-combo">${esc(T('c.t.add_combo'))}</button>`;
  }

  function layoutSection(): string {
    return '<div id="layout-host"></div>';
  }

  function policiesSection(): string {
    const cur: string = state?.currency ?? 'USD';
    const rows = doc.policies
      .map(
        (p: any, i: number) => `<section class="cfg-card">
          <div class="cfg-row">
            <label>${esc(T('c.p.name'))} ${input(`policies.${i}.name`, 'text', 'maxlength="80"')}</label>
            <label>${esc(T('c.p.requires'))} <select data-path="policies.${i}.kind" data-type="text">
              ${['none', 'card_hold', 'deposit'].map((v) => `<option value="${v}" ${p.kind === v ? 'selected' : ''}>${esc(T(`c.p.kind.${v}`))}</option>`).join('')}</select></label>
            <button class="danger" data-do="del-policy" data-i="${i}">${esc(T('common.remove'))}</button>
          </div>
          <div class="cfg-row">
            <label>${esc(T('c.s.party_from'))} ${input(`policies.${i}.minParty`, 'int', 'min="1" style="width:70px"')}</label>
            <label>${esc(T('c.s.to'))} ${input(`policies.${i}.maxParty`, 'int', `min="1" placeholder="${esc(T('c.p.any_ph'))}" style="width:70px"`)}</label>
            <label>${esc(T('c.p.amount'))} ${input(`policies.${i}.amountMinor`, 'money', 'style="width:90px"')}</label>
            <label><select data-path="policies.${i}.amountType" data-type="text">
              ${['per_guest', 'flat'].map((v) => `<option value="${v}" ${p.amountType === v ? 'selected' : ''}>${esc(T(`c.p.${v}`))}</option>`).join('')}</select></label>
          </div>
          <div class="cfg-row">
            <label>${esc(T('c.p.cutoff'))} ${input(`policies.${i}.cancelCutoffHours`, 'int', 'min="0" style="width:80px"')}</label>
            <label>${esc(T('c.p.charge_on'))} <select data-path="policies.${i}.chargeOn" data-type="text">
              ${['both', 'no_show', 'late_cancel'].map((v) => `<option value="${v}" ${p.chargeOn === v ? 'selected' : ''}>${esc(T(`c.p.on.${v}`))}</option>`).join('')}</select></label>
          </div>
          <label>${esc(T('c.p.message'))} ${input(`policies.${i}.message`, 'text', 'maxlength="500"')}</label>
          ${LOCALES.map(
            (l) =>
              `<label>${esc(T('c.p.message_in', { language: LOCALE_NAMES[l] }))} ${input(`policies.${i}.messages.${l}`, 'opt', 'maxlength="500"')}</label>`,
          ).join('')}
          <p class="muted small">${esc(
            p.kind === 'none'
              ? T('c.p.no_payment')
              : T('c.p.summary', {
                  from: p.minParty,
                  to: p.maxParty ? `–${p.maxParty}` : '+',
                  amount: money(p.amountMinor, cur),
                  each: p.amountType === 'per_guest' ? T('c.p.each') : '',
                }),
          )}</p>
        </section>`,
      )
      .join('');
    return `${rows || `<p class="muted">${esc(T('c.p.none'))}</p>`}<button class="primary" data-do="add-policy">${esc(T('c.p.add'))}</button>`;
  }

  function closuresSection(): string {
    const rows = doc.specialDays
      .map(
        (d: any, i: number) => `<tr>
          <td>${input(`specialDays.${i}.day`, 'text', 'placeholder="YYYY-MM-DD" style="width:130px"')}</td>
          <td>${input(`specialDays.${i}.title`, 'nullable', `maxlength="80" placeholder="${esc(T('c.cl.title_ph'))}"`)}</td>
          <td>${check(`specialDays.${i}.closed`, T('c.cl.closed'))}</td>
          <td><button class="ghost" data-do="copy-day" data-i="${i}" title="${esc(T('c.cl.copy_title'))}">${esc(T('c.cl.copy'))}</button>
              <button class="ghost" data-do="del-day" data-i="${i}" aria-label="${esc(T('c.s.remove_aria'))}">×</button></td></tr>`,
      )
      .join('');
    return `${specialTools()}<table class="cfg-table"><thead><tr><th>${esc(T('c.cl.date'))}</th><th>${esc(T('c.cl.title'))}</th><th></th><th></th></tr></thead><tbody>${rows}</tbody></table>
      <button class="primary" data-do="add-day">${esc(T('c.cl.add'))}</button>`;
  }

  function settingsSection(): string {
    const s = doc.settings;
    const offered: string[] = s.locales ?? [...LOCALES];
    return `<div class="cfg-card"><div class="cfg-row">
      <label>${esc(T('c.st.interval'))}<select data-path="settings.slotInterval" data-type="int">${[5, 10, 15, 20, 30, 60].map((m) => `<option value="${m}" ${s.slotInterval === m ? 'selected' : ''}>${m}</option>`).join('')}</select></label>
      <label>${esc(T('c.st.turn'))} ${input('settings.defaultTurnMinutes', 'int', 'min="15"')}</label>
      <label>${esc(T('c.st.large'))} ${input('settings.largePartyThreshold', 'int', `min="2" placeholder="${esc(T('c.st.off_ph'))}"`)}</label>
      <label>${esc(T('c.st.release'))} ${timeSelect('settings.releaseTime')}</label>
      <label>${esc(T('c.st.wl_from'))} ${input('settings.waitlistMinParty', 'int', 'min="1"')}</label>
      <label>${esc(T('c.st.wl_to'))} ${input('settings.waitlistMaxParty', 'int', 'min="1"')}</label>
    </div>
    <div class="cfg-row">
      ${check('settings.emailConfirmations', T('c.st.emails'))}
      <label>${esc(T('c.st.reminder'))} <select data-path="settings.reminderHours" data-type="int">${[0, 2, 4, 12, 24, 48].map((v) => `<option value="${v}" ${s.reminderHours === v ? 'selected' : ''}>${esc(T(`c.st.rem.${v}`))}</option>`).join('')}</select></label>
    </div>
    <div class="cfg-row">
      <label>${esc(T('c.st.language'))} <select data-path="settings.defaultLocale" data-type="text">${LOCALES.map((l) => `<option value="${l}" ${s.defaultLocale === l ? 'selected' : ''}>${esc(LOCALE_NAMES[l])}</option>`).join('')}</select></label>
      <span class="cfg-label">${esc(T('c.st.offered'))}</span>
      ${LOCALES.map(
        (l) =>
          `<label class="cfg-check"><input type="checkbox" data-set="locale" data-id="${l}" ${offered.includes(l) ? 'checked' : ''} ${l === s.defaultLocale ? 'disabled' : ''}> ${esc(LOCALE_NAMES[l])}</label>`,
      ).join('')}
    </div>
    <div class="cfg-row">
      <label>${esc(T('c.st.horizon'))} <input type="number" min="1" max="730" data-path="settings.horizonDays" data-type="int" value="${s.horizonDays ?? 90}"></label>
      <label style="flex:1 1 260px">${esc(T('c.st.notify'))} <input data-path="settings.notifyEmails" data-type="emails" value="${esc((s.notifyEmails ?? []).join(', '))}" placeholder="chef@restaurant.com, manager@restaurant.com"></label>
    </div>
    <p class="muted small">${esc(T('c.st.horizon_hint', { date: horizonDate(s) }))} ${esc(T('c.st.notify_hint'))}</p>
    <div class="cfg-row">${(['new_online', 'cancellation', 'large_party'] as const)
      .map((ev) => `<label class="cfg-check"><input type="checkbox" data-set="notify-ev" data-id="${ev}" ${(s.notifyEvents ?? []).includes(ev) ? 'checked' : ''}> ${esc(T(`c.st.notify_ev.${ev}`))}</label>`)
      .join('')}</div>
    <h4>${esc(T('c.st.daily'))}</h4>
    <div class="cfg-row"><label>${esc(T('c.st.daily_time'))} <select data-path="settings.dailySheetTime" data-type="nullable"><option value="" ${!s.dailySheetTime ? 'selected' : ''}>${esc(T('c.st.daily_off'))}</option>${TIMES.filter((x) => x >= '05:00' && x <= '12:00')
      .map((x) => `<option value="${x}" ${s.dailySheetTime === x ? 'selected' : ''}>${x}</option>`).join('')}</select></label></div>
    <p class="muted small">${esc(T('c.st.daily_hint'))}</p>
    <h4>${esc(T('c.st.channels'))}</h4>
    <label class="cfg-check"><input type="checkbox" data-path="settings.smsEnabled" data-type="bool" ${s.smsEnabled ? 'checked' : ''}> ${esc(T('c.st.sms'))}</label>
    <p class="muted small">${esc(T('c.st.sms_hint'))}</p>
    <label class="cfg-check"><input type="checkbox" data-path="settings.marketingEnabled" data-type="bool" ${s.marketingEnabled ? 'checked' : ''}> ${esc(T('c.st.marketing'))}</label>
    <p class="muted small">${esc(T('c.st.marketing_hint'))}</p>
    <p class="muted small">${esc(T('c.st.language_hint'))}</p>
    <p class="muted small">${esc(T('c.st.quick_hint'))}</p></div>`;
  }

  /** The last day guests can book online right now, in the restaurant's time zone. */
  function horizonDate(st: any): string {
    const tz = state?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());
    const [h, m] = String(st.releaseTime ?? '00:00').split(':').map(Number);
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
    const nowMin = Number(parts.find((p) => p.type === 'hour')?.value) * 60 + Number(parts.find((p) => p.type === 'minute')?.value);
    const open = nowMin >= h * 60 + m;
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + (st.horizonDays ?? 90) - (open ? 0 : 1));
    return new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { dateStyle: 'medium', timeZone: 'UTC' }).format(d);
  }

  /** Contrast of white text on a colour (WCAG), so a too-light accent colour is flagged before guests meet it. */
  function contrastWithWhite(hex: string): number | null {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m) return null;
    const c = [0, 2, 4].map((i) => {
      const v = parseInt(m[1].slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 1.05 / (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2] + 0.05);
  }

  function brandingSection(): string {
    const kinds = EMAIL_KINDS.map(
      (k) => `<option value="${k}" ${k === emailKind ? 'selected' : ''}>${esc(T(`c.b.kind.${k}`))}</option>`,
    ).join('');
    const langs = LOCALES.map(
      (l) => `<option value="${l}" ${l === emailLocale ? 'selected' : ''}>${esc(LOCALE_NAMES[l])}</option>`,
    ).join('');
    const tp = `emailTemplates.${emailKind}.${emailLocale}`;
    return `
      <div class="cfg-card">
        <h3 style="margin-top:0">${esc(T('c.b.business'))}</h3>
        <p class="muted small">${esc(T('c.b.business_hint'))}</p>
        <div class="cfg-row">
          <label style="flex:1 1 260px">${esc(T('c.b.name'))} ${input('settings.name', 'text', 'maxlength="120" required')}</label>
          <label style="flex:1 1 260px">${esc(T('c.b.legal'))} ${input('branding.legalName', 'nullable', 'maxlength="120"')}</label>
        </div>
        <div class="cfg-row">
          <label style="flex:1 1 260px">${esc(T('c.b.address'))} ${input('branding.address', 'nullable', 'maxlength="200"')}</label>
          <label>${esc(T('c.b.phone'))} ${input('branding.phone', 'nullable', 'maxlength="40"')}</label>
        </div>
        <div class="cfg-row">
          <label style="flex:1 1 220px">${esc(T('c.b.contact_email'))} ${input('branding.contactEmail', 'nullable', 'maxlength="200" type="email"')}</label>
          <label style="flex:1 1 220px">${esc(T('c.b.website'))} ${input('branding.website', 'nullable', 'maxlength="500" placeholder="https://"')}</label>
        </div>
        <label>${esc(T('c.b.review_url'))} ${input('branding.reviewUrl', 'nullable', 'maxlength="500" placeholder="https://"')}</label>
        <p class="muted small">${esc(T('c.b.review_hint'))}</p>
      </div>
      ${profileCard()}
      <div class="cfg-card">
        <p class="muted small">${esc(T('c.b.intro'))}</p>
        <div class="cfg-row">
          <label>${esc(T('c.b.accent'))} ${input('branding.accentColor', 'color')}</label>
          ${(() => {
            const c = contrastWithWhite(String(getPath('branding.accentColor') ?? ''));
            return c != null && c < 4.5 ? `<p class="cfg-bad small" style="flex:1 1 280px;margin:0">${esc(T('c.b.accent_warn', { ratio: c.toFixed(1) }))}</p>` : '';
          })()}
        </div>
        ${logoBlock()}
        ${LOCALES.map(
          (l) => `<label>${esc(T('c.b.footer', { language: LOCALE_NAMES[l] }))} ${input(`branding.footerNote.${l}`, 'opt', 'maxlength="400"')}</label>`,
        ).join('')}
        <p class="muted small">${esc(T('c.b.footer_hint'))}</p>
      </div>
      <div class="cfg-card">
        <h3 style="margin-top:0">${esc(T('c.b.wording'))}</h3>
        <p class="muted small">${esc(T('c.b.wording_hint'))}</p>
        <div class="email-split">
        <div class="email-form">
        <div class="cfg-row">
          <label>${esc(T('c.b.kind'))} <select data-ui="email-kind">${kinds}</select></label>
          <label>${esc(T('c.b.language'))} <select data-ui="email-locale">${langs}</select></label>
          <label class="cfg-check"><input type="checkbox" data-ui="preview-policy" ${previewPolicy ? 'checked' : ''}> ${esc(T('c.b.preview_policy'))}</label>
          ${emailKind === 'cancellation' ? `<label class="cfg-check"><input type="checkbox" data-ui="preview-fee" ${previewFee ? 'checked' : ''}> ${esc(T('c.b.preview_fee'))}</label>` : ''}
        </div>
        <label>${esc(T('c.b.subject'))} ${input(`${tp}.subject`, 'opt', 'maxlength="150"')}</label>
        <label>${esc(T('c.b.lead'))} <textarea data-path="${tp}.lead" data-type="opt" rows="3" maxlength="1000">${esc(getPath(`${tp}.lead`) ?? '')}</textarea></label>
        <div class="cfg-row"><button data-do="reset-wording">${esc(T('c.b.reset'))}</button></div>
        </div>
        <div class="email-side"><h4 style="margin-top:0">${esc(T('c.b.preview'))}</h4>
        <div id="email-preview" class="email-preview"><p class="muted small">${esc(T('c.loading'))}</p></div></div>
        </div>
      </div>`;
  }

  // ------------------------------------------------------------ logo
  const logoAddress = () => {
    const id = getPath('branding.logoAssetId');
    if (id) return `/v1/restaurants/${opts.restaurantId}/assets/${id}`;
    return (getPath('branding.logoUrl') as string | null) ?? null;
  };

  function logoBlock(): string {
    const src = logoAddress();
    const has = !!getPath('branding.logoAssetId');
    return `<div class="logo-box">
      <div class="logo-thumb">${src ? `<img src="${esc(src)}" alt="" referrerpolicy="no-referrer">` : `<span class="muted small">${esc(T('c.b.logo_none'))}</span>`}</div>
      <div class="logo-actions">
        <strong>${esc(T('c.b.logo_upload'))}</strong>
        <input type="file" id="logo-file" accept="image/png,image/jpeg,image/webp,image/gif" hidden>
        <div class="cfg-row">
          <button data-do="logo-pick" class="primary">${esc(T(has ? 'c.b.logo_replace' : 'c.b.logo_choose'))}</button>
          ${src ? `<button data-do="logo-remove">${esc(T('c.b.logo_remove'))}</button>` : ''}
        </div>
        <p class="muted small">${esc(T('c.b.logo_rules'))}</p>
        <label class="small">${esc(T('c.b.logo_or_link'))} ${input('branding.logoUrl', 'nullable', 'maxlength="500" placeholder="https://"')}</label>
      </div>
    </div>`;
  }

  function profileCard(): string {
    const gallery = (getPath('branding.profile.gallery') as string[] | undefined) ?? [];
    const cuisines = ((getPath('branding.profile.cuisines') as string[] | undefined) ?? []).join(', ');
    return `<div class="cfg-card">
      <h3 style="margin-top:0">${esc(T('c.p.title'))}</h3>
      <p class="muted small">${esc(T('c.p.hint'))}</p>
      <div class="cfg-row">
        <label style="flex:1 1 260px">${esc(T('c.p.cuisines'))} <input type="text" data-cuisines value="${esc(cuisines)}" maxlength="240"></label>
        <label style="flex:1 1 200px">${esc(T('c.p.check'))} ${input('branding.profile.typicalCheck', 'nullable', `maxlength="60" placeholder="${esc(T('c.p.check_ph'))}"`)}</label>
      </div>
      ${LOCALES.map((l) => `<label>${esc(T('c.p.description', { language: LOCALE_NAMES[l] }))} <textarea data-path="branding.profile.description.${l}" data-type="opt" rows="3" maxlength="1500">${esc(getPath(`branding.profile.description.${l}`) ?? '')}</textarea></label>`).join('')}
      <h4>${esc(T('c.p.gallery'))}</h4>
      <div class="photo-grid">${gallery
        .map(
          (a, i) => `<div class="photo"><img src="/v1/restaurants/${opts.restaurantId}/assets/${esc(a)}" alt="">
            <div class="cfg-row">
              <button data-do="photo-left" data-i="${i}" title="${esc(T('c.p.left'))}" ${i === 0 ? 'disabled' : ''}>←</button>
              <button data-do="photo-right" data-i="${i}" title="${esc(T('c.p.right'))}" ${i === gallery.length - 1 ? 'disabled' : ''}>→</button>
              <button data-do="photo-del" data-i="${i}">${esc(T('c.p.remove'))}</button>
            </div></div>`,
        )
        .join('')}</div>
      <input type="file" id="photo-file" accept="image/png,image/jpeg,image/webp,image/gif" hidden>
      <div class="cfg-row"><button data-do="photo-pick" class="primary" ${gallery.length >= 8 ? 'disabled' : ''}>${esc(T('c.p.photo_add'))}</button></div>
      <p class="muted small">${esc(T('c.p.gallery_hint'))}</p>
    </div>`;
  }

  async function uploadPhoto(file: File) {
    if (file.size > 1024 * 1024) return opts.toast(T('c.p.big'), true);
    opts.toast(T('c.b.logo_uploading'));
    try {
      const res = await fetch(`/v1/restaurants/${opts.restaurantId}/assets/photo`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const e = new Error(json?.error?.message ?? 'Upload failed') as ApiError;
        e.code = json?.error?.code ?? 'error';
        throw e;
      }
      const g = ((getPath('branding.profile.gallery') as string[] | undefined) ?? []).slice();
      g.push(json.assetId);
      setPath('branding.profile.gallery', g);
      scheduleSave();
      render();
    } catch (e) {
      err(e);
    }
  }

  async function uploadLogo(file: File) {
    if (file.size > LOGO_MAX) return opts.toast(T('c.b.logo_big'), true);
    opts.toast(T('c.b.logo_uploading'));
    try {
      const res = await fetch(`/v1/restaurants/${opts.restaurantId}/assets/logo`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const e = new Error(json?.error?.message ?? 'Upload failed') as ApiError;
        e.code = json?.error?.code ?? 'error';
        throw e;
      }
      setPath('branding.logoAssetId', json.assetId);
      setPath('branding.logoUrl', null);
      scheduleSave();
      render();
      opts.toast(T('c.b.logo_ready'));
    } catch (e) {
      err(e);
    }
  }

  // ------------------------------------------------------------ booking page
  const select = (path: string, options: [string, string][], type: 'text' | 'int' = 'text') =>
    `<select data-path="${path}" data-type="${type}">${options
      .map(([v, l]) => `<option value="${esc(v)}" ${String(getPath(path)) === v ? 'selected' : ''}>${esc(l)}</option>`)
      .join('')}</select>`;

  /** A colour that can fall back to the theme's own. */
  const themeColor = (path: string, label: string, fallback: string) => {
    const v = getPath(path) as string | null;
    return `<div class="wp-color"><span class="cfg-label">${esc(label)}</span>
      <label class="cfg-check"><input type="checkbox" data-set="wcolor" data-target="${path}" data-fallback="${fallback}" ${v == null ? 'checked' : ''}> ${esc(T('c.w.theme_default'))}</label>
      ${v == null ? '' : `<input type="color" data-path="${path}" data-type="text" value="${esc(v)}">`}</div>`;
  };

  function embedBlock(): string {
    const origin = location.origin;
    const attrs = [`data-restaurant="${opts.restaurantId}"`];
    if (embedLang) attrs.push(`data-lang="${embedLang}"`);
    if (embedParty !== 2) attrs.push(`data-party="${embedParty}"`);
    if (embedHeight !== 640) attrs.push(`data-height="${embedHeight}"`);
    const script = `<script src="${origin}/embed.js" ${attrs.join(' ')}></script>`;
    const q = `rid=${opts.restaurantId}${embedLang ? `&lang=${embedLang}` : ''}`;
    const link = `${origin}/widget?${q}`;
    const wl = `${origin}/widget?${q}&mode=waitlist`;
    const qr = (target: string) =>
      `/v1/restaurants/${opts.restaurantId}/qr.svg?target=${target}${embedLang ? `&lang=${embedLang}` : ''}`;
    return `<div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.embed'))}</h3>
      <p class="muted small">${esc(T('c.w.embed_hint'))}</p>
      <div class="cfg-row">
        <label>${esc(T('c.w.preview_lang'))}<select data-ui="embed-lang"><option value="">${esc(T('c.w.embed_auto'))}</option>${LOCALES.map((l) => `<option value="${l}" ${embedLang === l ? 'selected' : ''}>${esc(LOCALE_NAMES[l])}</option>`).join('')}</select></label>
        <label>${esc(T('c.w.default_party'))}<input type="number" min="1" max="40" data-ui="embed-party" value="${embedParty}" style="width:90px"></label>
        <label>${esc(T('c.w.embed_height'))}<input type="number" min="320" max="1600" step="20" data-ui="embed-height" value="${embedHeight}" style="width:110px"></label>
      </div>
      <pre class="wp-code"><code>${esc(script)}</code></pre>
      <button data-do="copy" data-text="${esc(script)}">${esc(T('c.w.embed_copy'))}</button>
      <p class="small" style="margin-top:12px"><strong>${esc(T('c.w.embed_link'))}</strong><br><a href="${esc(link)}" target="_blank" rel="noopener">${esc(link)}</a></p>
      <div class="qr-row">
        <figure><img src="${esc(qr('booking'))}" alt="" width="140" height="140"><figcaption class="small">${esc(T('c.w.qr_booking'))}<br><a href="${esc(qr('booking'))}" download="booking-qr.svg">${esc(T('c.w.qr_download'))}</a></figcaption></figure>
        <figure><img src="${esc(qr('waitlist'))}" alt="" width="140" height="140"><figcaption class="small">${esc(T('c.w.qr_waitlist'))}<br><a href="${esc(qr('waitlist'))}" download="waitlist-qr.svg">${esc(T('c.w.qr_download'))}</a><br><a href="${esc(wl)}" target="_blank" rel="noopener">${esc(T('c.w.embed_link'))}</a></figcaption></figure>
      </div>
      <p class="muted small">${esc(T('c.w.embed_published'))}</p></div>`;
  }

  /** One-click looks and a way to copy a look between restaurants. */
  function looksBlock(): string {
    const l = getLocale();
    const cards = LOOK_PRESETS.map((p) => {
      const w = p.widget;
      const dark = w.theme === 'dark';
      const bg = w.background ?? (dark ? '#12161d' : '#ffffff');
      const panel = w.panel ?? (dark ? '#1a2029' : '#f6f7f9');
      const ink = w.textColor ?? (dark ? '#eef1f6' : '#1a2230');
      return `<div class="look-card">
        <div class="look-sw" style="background:${bg};color:${ink}">
          <span class="look-h" style="color:${p.accentColor};font-family:${w.font === 'serif' ? 'Georgia,serif' : w.font === 'mono' ? 'monospace' : 'system-ui,sans-serif'}">Aa</span>
          <span class="look-p" style="background:${panel};border-radius:${Math.min(w.radius ?? 8, 14)}px"></span>
          <span class="look-b" style="background:${w.buttonStyle === 'outline' ? 'transparent' : p.accentColor};border:2px solid ${p.accentColor};border-radius:${Math.min(w.radius ?? 8, 14)}px"></span>
        </div>
        <strong>${esc(p.name[l])}</strong>
        <span class="muted small">${esc(p.description[l])}</span>
        <button data-do="apply-look" data-id="${p.id}">${esc(T('c.w.look_apply'))}</button>
      </div>`;
    }).join('');
    return `<div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.looks'))}</h3>
      <p class="muted small">${esc(T('c.w.looks_hint'))}</p>
      <div class="look-grid">${cards}</div>
      <details><summary>${esc(T('c.w.look_share'))}</summary>
        <p class="muted small">${esc(T('c.w.look_json_hint'))}</p>
        <textarea id="look-json" class="wp-css" rows="5" spellcheck="false"></textarea>
        <div class="cfg-row"><button data-do="look-export">${esc(T('c.w.look_export'))}</button><button data-do="look-import">${esc(T('c.w.look_import'))}</button></div>
      </details></div>`;
  }

  function widgetSection(): string {
    const w = doc.branding.widget;
    const perLang = (key: 'title' | 'intro' | 'confirmLabel' | 'thanks', labelKey: string, max: number, area = false) =>
      LOCALES.map((l) => {
        const path = `branding.widget.${key}.${l}`;
        const label = esc(T(labelKey, { language: LOCALE_NAMES[l] }));
        return area
          ? `<label>${label} <textarea data-path="${path}" data-type="opt" rows="2" maxlength="${max}">${esc(getPath(path) ?? '')}</textarea></label>`
          : `<label>${label} ${input(path, 'opt', `maxlength="${max}"`)}</label>`;
      }).join('');
    const parties = Array.from({ length: 20 }, (_, i): [string, string] => [String(i + 1), String(i + 1)]);
    const days = Array.from({ length: 7 }, (_, i): [string, string] => [String(i + 1), String(i + 1)]);
    const times = TIMES.map((t): [string, string] => [t, t]);
    const langs = LOCALES.map((l) => `<option value="${l}" ${l === widgetLocale ? 'selected' : ''}>${esc(LOCALE_NAMES[l])}</option>`).join('');
    return `<p class="muted small">${esc(T('c.w.intro'))}</p>
    ${looksBlock()}
    <div class="wp-grid">
      <div class="wp-form">
        <div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.look'))}</h3>
          <div class="cfg-row">
            <label>${esc(T('c.w.theme'))} ${select('branding.widget.theme', [['light', T('c.w.theme.light')], ['dark', T('c.w.theme.dark')], ['auto', T('c.w.theme.auto')]])}</label>
            <label>${esc(T('c.w.font'))} ${select('branding.widget.font', (['system', 'serif', 'rounded', 'mono'] as const).map((f): [string, string] => [f, T(`c.w.font.${f}`)]))}</label>
            <label>${esc(T('c.w.width'))} ${select('branding.widget.width', (['narrow', 'normal', 'wide'] as const).map((f): [string, string] => [f, T(`c.w.width.${f}`)]))}</label>
          </div>
          <div class="cfg-row">
            <label>${esc(T('c.w.accent'))} ${input('branding.accentColor', 'color')}</label>
            <label>${esc(T('c.w.button'))} ${select('branding.widget.buttonStyle', [['solid', T('c.w.button.solid')], ['outline', T('c.w.button.outline')]])}</label>
            <label>${esc(T('c.w.align'))} ${select('branding.widget.headerAlign', [['left', T('c.w.align.left')], ['center', T('c.w.align.center')]])}</label>
            <label>${esc(T('c.w.radius'))} <input type="range" min="0" max="28" step="1" data-path="branding.widget.radius" data-type="int" value="${w.radius}"></label>
          </div>
          ${themeColor('branding.widget.background', T('c.w.background'), '#ffffff')}
          ${themeColor('branding.widget.panel', T('c.w.panel'), '#f6f7f9')}
          ${themeColor('branding.widget.textColor', T('c.w.text'), '#1a2230')}
          <div class="cfg-row">
            ${check('branding.widget.showLogo', T('c.w.show_logo'))}
            ${check('branding.widget.showName', T('c.w.show_name'))}
          </div>
          <p class="muted small">${esc(T('c.b.logo_rules'))}</p>
          <div class="cfg-row"><button data-do="goto-branding">${esc(T('c.b.logo_upload'))} →</button></div>
        </div>
        <div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.texts'))}</h3>
          ${perLang('title', 'c.w.title', 80)}
          <p class="muted small">${esc(T('c.w.title_hint'))}</p>
          ${perLang('intro', 'c.w.intro_text', 300, true)}
          ${perLang('confirmLabel', 'c.w.confirm_label', 40)}
          ${perLang('thanks', 'c.w.thanks', 500, true)}
        </div>
        <div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.fields'))}</h3>
          <div class="cfg-row">
            <label>${esc(T('c.w.phone'))} ${select('branding.widget.fields.phone', (['hidden', 'optional', 'required'] as const).map((f): [string, string] => [f, T(`c.w.phone.${f}`)]))}</label>
          </div>
          <div class="cfg-row" style="flex-direction:column;align-items:flex-start">
            ${check('branding.widget.fields.occasion', T('c.w.occasion'))}
            ${check('branding.widget.fields.request', T('c.w.request'))}
            ${check('branding.widget.fields.marketing', T('c.w.marketing'))}
            ${check('branding.widget.fields.sms', T('c.w.sms'))}
            ${check('branding.widget.fields.area', T('c.w.area'))}
          </div>
        </div>
        <div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.behaviour'))}</h3>
          <div class="cfg-row">
            <label>${esc(T('c.w.default_party'))} ${select('branding.widget.defaultParty', parties, 'int')}</label>
            <label>${esc(T('c.w.days_shown'))} ${select('branding.widget.daysShown', days, 'int')}</label>
            <label>${esc(T('c.w.time_from'))} ${select('branding.widget.timeFrom', times)}</label>
            <label>${esc(T('c.w.time_to'))} ${select('branding.widget.timeTo', times)}</label>
          </div>
          <p class="muted small">${esc(T('c.w.behaviour_hint'))}</p>
        </div>
        <div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.terms'))}</h3>
          ${check('branding.widget.termsRequired', T('c.w.terms_required'))}
          ${check('branding.widget.showMenus', T('c.w.show_menus'))}
          ${LOCALES.map((l) => `<label>${esc(T('c.w.terms_text', { language: LOCALE_NAMES[l] }))} ${input(`branding.widget.termsText.${l}`, 'opt', 'maxlength="500"')}</label>`).join('')}
          <p class="muted small">${esc(T('c.w.terms_hint'))}</p>
        </div>
        <div class="cfg-card"><h3 style="margin-top:0">${esc(T('c.w.css'))}</h3>
          <textarea class="wp-css" data-path="branding.widget.customCss" data-type="text" rows="6" maxlength="4000" spellcheck="false" placeholder=".slot { font-weight: 700; }">${esc(w.customCss ?? '')}</textarea>
          <p class="muted small">${esc(T('c.w.css_hint'))}</p>
        </div>
        ${embedBlock()}
      </div>
      <aside class="wp-preview" aria-label="${esc(T('c.w.preview'))}">
        <div class="cfg-row"><strong>${esc(T('c.w.preview'))}</strong>
          <label>${esc(T('c.w.preview_lang'))} <select data-ui="widget-locale">${langs}</select></label></div>
        <iframe id="wp-frame" title="${esc(T('c.w.preview'))}" src="/widget?rid=${opts.restaurantId}&preview=1&lang=${widgetLocale}"></iframe>
      </aside>
    </div>`;
  }

  /** The draft's branding as the booking page expects it (logo address resolved). */
  const previewBranding = () => ({ ...doc.branding, logoUrl: logoAddress() });

  function pushPreview() {
    const f = root.querySelector('#wp-frame') as HTMLIFrameElement | null;
    if (!f?.contentWindow || !doc) return;
    f.contentWindow.postMessage(
      { type: 'preview-branding', branding: previewBranding(), locale: widgetLocale, name: doc.settings.name },
      location.origin,
    );
  }
  function schedulePush() {
    clearTimeout(widgetTimer);
    widgetTimer = setTimeout(pushPreview, 120);
  }
  const onFrameMessage = (e: MessageEvent) => {
    if (e.origin !== location.origin) return;
    if ((e.data as any)?.source === 'table-reservations' && (e.data as any)?.type === 'preview-ready') pushPreview();
  };
  window.addEventListener('message', onFrameMessage);

  function schedulePreview() {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(loadPreview, 450);
  }

  async function loadPreview() {
    const box = root.querySelector('#email-preview');
    if (!box || !doc || section !== 'branding') return;
    try {
      const r = await api<any>('POST', `/v1/restaurants/${opts.restaurantId}/emails/preview`, {
        kind: emailKind,
        locale: emailLocale,
        branding: doc.branding,
        templates: doc.emailTemplates,
        restaurantName: doc.settings.name || undefined,
        withFee: emailKind === 'cancellation' && previewFee,
        withPolicy: previewPolicy,
      });
      // Start from the wording that is used today, so it can be edited instead of written from scratch.
      // Text equal to the standard is not stored as an override (see applyField), so it keeps following the standard.
      for (const [sel, text] of [['.email-form [data-path$=".subject"]', r.template?.subject], ['.email-form [data-path$=".lead"]', r.template?.lead]] as const) {
        const el = root.querySelector(sel) as HTMLInputElement | HTMLTextAreaElement | null;
        if (!el || !text) continue;
        el.dataset.default = String(text);
        el.placeholder = String(text);
        if (el.value === '' && document.activeElement !== el) el.value = String(text);
      }
      box.innerHTML = `<div class="email-subject"><span class="muted small">${esc(T('c.b.subject_label'))}</span><strong>${esc(r.subject)}</strong></div>
        <iframe sandbox title="${esc(T('c.b.preview'))}" class="email-frame" srcdoc="${esc(r.html)}"></iframe>`;
    } catch {
      box.innerHTML = `<p class="cfg-bad small">${esc(T('c.b.preview_failed'))}</p>`;
    }
  }

  function historySection(): string {
    const rows = state.versions
      .map(
        (v: any) => `<tr>
          <td>v${v.version}${v.version === state.liveVersion ? ` <span class="chip">${esc(T('c.h.live'))}</span>` : ''}</td>
          <td>${esc(new Date(v.publishedAt).toLocaleString(LOCALE_TAGS[getLocale()]))}</td>
          <td>${esc(String(v.publishedBy).replace(/^staff:/, ''))}</td>
          <td>${esc(v.note ?? '')}</td>
          <td>${v.version === state.liveVersion ? '' : `<button data-do="restore" data-v="${v.version}">${esc(T('c.h.restore'))}</button>`}</td></tr>`,
      )
      .join('');
    return `<table class="cfg-table"><thead><tr><th>${esc(T('c.h.version'))}</th><th>${esc(T('c.h.published'))}</th><th>${esc(T('c.h.by'))}</th><th>${esc(T('c.h.note'))}</th><th></th></tr></thead><tbody>${rows}</tbody></table>
      <p class="muted small">${esc(T('c.h.hint'))}</p>`;
  }

  // ------------------------------------------------------------ review panel
  function reviewPanel(): string {
    if (!draft) return '';
    const d = draft.diff;
    const lines: string[] = [];
    if (d.settings.length) {
      const names = d.settings.map((k: string) => {
        const key = `c.d.${k}`;
        return T(key) === key ? k : T(key);
      });
      lines.push(`<li><strong>${esc(T('c.d.settings'))}</strong> ${esc(T('c.d.changed_fields', { list: names.join(', ') }))}</li>`);
    }
    for (const [k, c] of Object.entries(d.collections) as [string, any][]) {
      for (const [kind, names] of [['added', c.added], ['changed', c.changed], ['removed', c.removed]] as [string, string[]][])
        if (names.length)
          lines.push(`<li class="d-${kind}"><strong>${esc(T(`c.d.${k}`))}</strong> ${esc(T(`c.d.${kind}`))}: ${names.map(esc).join(', ')}</li>`);
    }
    const issues = (xs: any[], cls: string) => xs.map((x) => `<li class="${cls}">${esc(x.message)}</li>`).join('');
    return `<aside class="cfg-review">
      <h3>${esc(T('c.review'))}</h3>
      ${draft.stale ? `<p class="cfg-bad">${esc(T('c.stale'))}</p>` : ''}
      ${lines.length ? `<ul>${lines.join('')}</ul>` : `<p class="muted small">${esc(T('c.no_changes'))}</p>`}
      ${draft.errors.length ? `<h4>${esc(T('c.fix_first'))}</h4><ul>${issues(draft.errors, 'cfg-bad')}</ul>` : ''}
      ${draft.warnings.length ? `<h4>${esc(T('c.check'))}</h4><ul>${issues(draft.warnings, 'cfg-warn')}</ul>` : ''}
      <label>${esc(T('c.note'))} <input type="text" id="cfg-note" maxlength="200" value="${esc(note)}" placeholder="${esc(T('c.note_ph'))}"></label>
    </aside>`;
  }

  // ------------------------------------------------------------ rendering
  function barHtml(): string {
    const n = draft?.diff?.total ?? 0;
    const blocked = !draft || draft.errors.length > 0 || draft.stale || n === 0 || !opts.canPublish || busy || saving;
    return `<div class="cfg-bar">
      <strong>${esc(T('c.title'))}</strong>
      <span class="pill">${esc(state ? (state.liveVersion ? T('c.live_v', { v: state.liveVersion }) : T('c.live_initial')) : T('c.loading'))}</span>
      ${
        draft
          ? `<span class="pill" style="border-color:var(--warn,#d9a441)">${esc(TN('c.changes', n))}${saving ? esc(T('c.saving')) : ''}</span>`
          : `<span class="muted small">${esc(T('c.no_draft'))}</span>`
      }
      <span class="spacer"></span>
      ${draft ? `<button data-do="discard">${esc(T('c.discard'))}</button>` : ''}
      ${
        draft
          ? `<button class="primary" data-do="publish" ${blocked ? 'disabled' : ''} title="${opts.canPublish ? '' : esc(T('c.publish_needs_perm'))}">${esc(busy ? T('c.publishing') : T('c.publish'))}</button>`
          : ''
      }
      <button data-do="close">${esc(T('common.close'))}</button>
    </div>`;
  }

  function paintBar() {
    const bar = root.querySelector('.cfg-bar');
    if (bar) bar.outerHTML = barHtml();
    const rev = root.querySelector('.cfg-review');
    if (rev && draft) {
      const noteEl = root.querySelector('#cfg-note') as HTMLInputElement | null;
      const had = document.activeElement === noteEl;
      rev.outerHTML = reviewPanel();
      if (had) (root.querySelector('#cfg-note') as HTMLInputElement | null)?.focus();
    }
  }

  function render() {
    const nav = SECTIONS.map(
      (k) => `<button role="tab" data-do="nav" data-s="${k}" aria-selected="${k === section}">${esc(T(`c.sec.${k}`))}</button>`,
    ).join('');
    let body: string;
    if (!state) body = `<p class="muted">${esc(T('c.loading'))}</p>`;
    else if (section === 'history') body = historySection();
    else if (!doc) {
      body = `<div class="cfg-card"><p>${esc(T('c.start_intro'))}</p>
        <button class="primary" data-do="start">${esc(T('c.start'))}</button></div>`;
      body += liveSummary();
    } else
      body = {
        shifts: shiftsSection, schedule: scheduleSection, experiences: experiencesSection, areas: areasSection, tables: tablesSection, layout: layoutSection, policies: policiesSection,
        closures: closuresSection, settings: settingsSection, branding: brandingSection, widget: widgetSection, history: historySection,
      }[section]();
    root.innerHTML = `${barHtml()}
      <div class="cfg-body ${section === 'layout' ? 'cfg-wide' : ''}">
        <nav class="cfg-nav" role="tablist" aria-label="${esc(T('c.nav_aria'))}">${nav}</nav>
        <main class="cfg-main">${body}</main>
        ${draft ? reviewPanel() : ''}
      </div>`;
    const lh = root.querySelector('#layout-host') as HTMLElement | null;
    if (lh && doc) layout.mount(lh);
    if (section === 'branding' && doc) schedulePreview();
  }

  function liveSummary(): string {
    const l = state.live;
    return `<div class="cfg-card"><h3>${esc(T('c.live_now'))}</h3><ul>
      <li>${esc(T('c.live_shifts', { n: l.shifts.length, list: l.shifts.map((s: any) => `${s.name} ${s.first}–${s.last}`).join(', ') }))}</li>
      <li>${esc(T('c.live_tables', { t: l.tables.length, c: l.combos.length }))}</li>
      <li>${esc(T('c.live_policies', { n: l.policies.length, list: l.policies.map((p: any) => p.name).join(', ') || T('common.none') }))}</li>
      <li>${esc(T('c.live_closures', { n: l.specialDays.length }))}</li></ul></div>`;
  }

  // ------------------------------------------------------------ events
  root.addEventListener('input', (ev) => {
    const el = ev.target as HTMLInputElement;
    if (el.id === 'cfg-note') {
      note = el.value;
      return;
    }
    if (el.type === 'checkbox' || el.tagName === 'SELECT') return; // handled on change
    applyField(el);
  });

  root.addEventListener('change', (ev) => {
    const el = ev.target as HTMLInputElement;
    const ui = el.dataset.ui;
    if (ui) {
      if (ui === 'email-kind') emailKind = el.value as EmailKind;
      else if (ui === 'email-locale') emailLocale = el.value as Locale;
      else if (ui === 'preview-fee') previewFee = el.checked;
      else if (ui === 'preview-policy') previewPolicy = el.checked;
      else if (ui === 'widget-locale') widgetLocale = el.value as Locale;
      else if (ui === 'hol-country') holidayCountry = el.value as HolidayCountry;
      else if (ui === 'hol-year') holidayYear = Math.max(2020, Math.min(2100, Number(el.value) || holidayYear));
      else if (ui === 'embed-lang') embedLang = el.value;
      else if (ui === 'embed-party') embedParty = Math.max(1, Math.min(40, Number(el.value) || 2));
      else if (ui === 'embed-height') embedHeight = Math.max(320, Math.min(1600, Number(el.value) || 640));
      render();
      return;
    }
    if (el.id === 'photo-file') {
      const f = el.files?.[0];
      el.value = '';
      if (f) void uploadPhoto(f);
      return;
    }
    if (el.hasAttribute('data-cuisines')) {
      if (!doc) return;
      setPath('branding.profile.cuisines', el.value.split(',').map((v) => v.trim()).filter(Boolean).slice(0, 5));
      scheduleSave();
      return;
    }
    if (el.id === 'logo-file') {
      const f = el.files?.[0];
      el.value = '';
      if (f) void uploadLogo(f);
      return;
    }
    if (!doc) return;
    const set = el.dataset.set;
    if (set) {
      const i = Number(el.dataset.i);
      const toggle = (arr: string[], v: string, on: boolean) => (on ? [...new Set([...arr, v])] : arr.filter((x) => x !== v));
      if (set === 'shift-day') {
        const d = Number(el.dataset.day);
        doc.shifts[i].days = (el.checked ? [...new Set([...doc.shifts[i].days, d])] : doc.shifts[i].days.filter((x: number) => x !== d)).sort();
      } else if (set === 'shift-plan') doc.shifts[i].floorPlanIds = toggle(doc.shifts[i].floorPlanIds, el.dataset.id!, el.checked);
      else if (set === 'shift-table') {
        const id = el.dataset.id!;
        const rest = doc.shifts[i].tablePolicy.filter((x: any) => x.tableId !== id);
        doc.shifts[i].tablePolicy = el.checked ? rest : [...rest, { tableId: id, takesReservations: false }];
      } else if (set === 'combo-table') doc.combos[i].tableIds = toggle(doc.combos[i].tableIds, el.dataset.id!, el.checked);
      else if (set === 'wcolor') {
        setPath(el.dataset.target!, el.checked ? null : el.dataset.fallback!);
        scheduleSave();
        render();
        return;
      } else if (set === 'locale') {
        doc.settings.locales = toggle(doc.settings.locales ?? [...LOCALES], el.dataset.id!, el.checked);
        if (!doc.settings.locales.includes(doc.settings.defaultLocale)) doc.settings.locales.push(doc.settings.defaultLocale);
      } else if (set === 'flow-on') {
        doc.shifts[i].flow = el.checked ? { bucket: 15, maxCovers: 30, maxParties: null } : null;
        scheduleSave();
        render();
        return;
      }
      scheduleSave();
      return;
    }
    if (el.dataset.path) {
      applyField(el);
      // Selects that change what is shown need a repaint.
      const p = el.dataset.path;
      if (p.endsWith('.kind') || p.endsWith('.amountType') || p === 'settings.defaultLocale') {
        if (p === 'settings.defaultLocale') {
          const l = doc.settings.locales ?? [];
          if (!l.includes(doc.settings.defaultLocale)) doc.settings.locales = [...l, doc.settings.defaultLocale];
        }
        render();
      }
    }
  });

  function applyField(el: HTMLInputElement | HTMLTextAreaElement) {
    const path = el.dataset.path;
    if (!path || !doc) return;
    const t = el.dataset.type;
    let v: unknown = el.value;
    if (t === 'int')
      v = el.value === ''
        ? path.endsWith('maxOnlineParty') || path.endsWith('maxParties') || path.endsWith('maxParty') || path.endsWith('largePartyThreshold')
          ? null
          : 0
        : Number(el.value);
    else if (t === 'money') v = Math.round(Number(el.value || 0) * 100);
    else if (t === 'bool') v = (el as HTMLInputElement).checked;
    else if (t === 'emails') v = el.value.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
    else if (t === 'nullable') v = el.value.trim() === '' ? null : el.value;
    else if (t === 'opt') v = el.value.trim() === '' || (el.dataset.default !== undefined && el.value === el.dataset.default) ? undefined : el.value;
    else if (t === 'text' && /\.(title|message)$/.test(path) && el.value === '') v = null;
    setPath(path, v);
    scheduleSave();
    if (path.startsWith('branding.') || path.startsWith('emailTemplates.')) schedulePreview();
  }

  root.addEventListener('click', async (ev) => {
    const b = (ev.target as HTMLElement).closest('[data-do]') as HTMLElement | null;
    if (!b) return;
    const act = b.dataset.do!;
    const i = Number(b.dataset.i);
    const edit = (fn: () => void) => {
      if (!doc) return;
      fn();
      scheduleSave();
      render();
    };
    switch (act) {
      case 'close':
        clearTimeout(saveTimer);
        if (saving) await save();
        window.removeEventListener('message', onFrameMessage);
        root.remove();
        document.body.classList.remove('cfg-open');
        opts.onClose();
        return;
      case 'apply-look': {
        const p = LOOK_PRESETS.find((x) => x.id === b.dataset.id);
        if (!p || !doc) return;
        if (!confirm(T('c.w.look_confirm', { name: p.name[getLocale()] }))) return;
        const look = lookOf(p, doc.branding.widget);
        doc.branding.accentColor = look.accentColor;
        doc.branding.widget = look.widget;
        doc.emailTemplates = structuredClone(p.emailTemplates);
        scheduleSave();
        render();
        opts.toast(T('c.w.look_applied', { name: p.name[getLocale()] }));
        return;
      }
      case 'look-export': {
        const box = root.querySelector('#look-json') as HTMLTextAreaElement | null;
        if (box && doc)
          box.value = JSON.stringify(
            { accentColor: doc.branding.accentColor, widget: { ...doc.branding.widget }, emailTemplates: doc.emailTemplates },
            null,
            2,
          );
        return;
      }
      case 'look-import': {
        const box = root.querySelector('#look-json') as HTMLTextAreaElement | null;
        if (!box || !doc) return;
        try {
          const o = JSON.parse(box.value);
          if (!o || typeof o !== 'object' || typeof o.widget !== 'object') throw new Error('shape');
          if (typeof o.accentColor === 'string') doc.branding.accentColor = o.accentColor;
          doc.branding.widget = { ...doc.branding.widget, ...o.widget };
          if (o.emailTemplates && typeof o.emailTemplates === 'object') doc.emailTemplates = o.emailTemplates;
        } catch {
          return opts.toast(T('c.w.look_bad'), true);
        }
        scheduleSave();
        render();
        opts.toast(T('c.w.look_imported'));
        return;
      }
      case 'x-add':
        experiences = [
          ...(experiences ?? []),
          { id: null, titles: { [getLocale()]: T('c.x.new') }, descriptions: {}, priceMinor: 0, days: [1, 2, 3, 4, 5, 6, 7], dateFrom: null, dateTo: null,
            startTime: '18:00', endTime: '21:00', minParty: 1, maxParty: 12, maxCovers: null, active: true, accessCode: null, accessTags: [] },
        ];
        return render();
      case 'x-save': {
        const x = (experiences ?? [])[i];
        if (!x) return;
        try {
          const body = readExperience(i);
          const r = x.id
            ? await api<any>('PUT', `/v1/restaurants/${opts.restaurantId}/experiences/${x.id}`, body)
            : await api<any>('POST', `/v1/restaurants/${opts.restaurantId}/experiences`, body);
          experiences = r.experiences;
          opts.toast(T('c.x.saved'));
          render();
        } catch (e) {
          const ex = e as ApiError;
          if (ex.status === 422 && ex.details?.length) opts.toast(ex.details.map((d: any) => d.message).slice(0, 2).join(' · '), true);
          else err(e);
        }
        return;
      }
      case 'x-del': {
        const x = (experiences ?? [])[i];
        if (!x) return;
        if (x.id) {
          if (!confirm(T('c.x.delete_confirm'))) return;
          try {
            experiences = (await api<any>('DELETE', `/v1/restaurants/${opts.restaurantId}/experiences/${x.id}`)).experiences;
          } catch (e) {
            return err(e);
          }
        } else experiences = (experiences ?? []).filter((_, k) => k !== i);
        return render();
      }
      case 'add-shift-grid':
        return edit(() => {
          const sh = {
            id: uid(), name: T('c.sg.new'), color: '#2b7a9b', days: [1, 2, 3, 4, 5, 6, 7], first: '18:00', last: '21:00', maxOnlineParty: null,
            specialDayId: null, floorPlanIds: doc.floorPlans.map((f: any) => f.id), tablePolicy: [], flow: null,
            turnRules: [{ min: 1, max: 99, minutes: doc.settings.defaultTurnMinutes }],
          };
          doc.shifts.push(sh);
          selShift = sh.id;
        });
      case 'toggle-range':
        showRange = !showRange;
        return render();
      case 'toggle-holidays':
        showHolidays = !showHolidays;
        return render();
      case 'range-apply': {
        const from = (root.querySelector('#rg-from') as HTMLInputElement).value;
        const to = (root.querySelector('#rg-to') as HTMLInputElement).value;
        const title = (root.querySelector('#rg-title') as HTMLInputElement).value.trim() || null;
        if (!from || !to || to < from) return opts.toast(T('c.cl.range_bad'), true);
        const days: string[] = [];
        for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`) && days.length < 366; d = new Date(d.getTime() + 86400_000))
          days.push(d.toISOString().slice(0, 10));
        const have = new Set<string>(doc.specialDays.map((d: any) => d.day));
        return edit(() => {
          for (const day of days) {
            const ex = doc.specialDays.find((d: any) => d.day === day);
            if (ex) {
              ex.closed = true;
              if (title) ex.title = title;
            } else if (!have.has(day)) doc.specialDays.push({ id: uid(), day, title, closed: true });
          }
          showRange = false;
        });
      }
      case 'hol-apply': {
        const picks = [...root.querySelectorAll<HTMLInputElement>('[data-hol]')].filter((c) => c.checked && !c.disabled);
        return edit(() => {
          for (const c of picks) doc.specialDays.push({ id: uid(), day: c.dataset.hol!, title: c.dataset.name!, closed: true });
          showHolidays = false;
          opts.toast(T('c.cl.hol_added', { n: picks.length }));
        });
      }
      case 'copy-day': {
        const src = doc.specialDays[i];
        if (!src) return;
        const to = window.prompt(T('c.cl.copy_prompt'), src.day)?.trim();
        if (!to || !/^\d{4}-\d{2}-\d{2}$/.test(to) || to === src.day) return;
        if (doc.specialDays.some((d: any) => d.day === to)) return opts.toast(T('c.cl.copy_exists'), true);
        return edit(() => {
          const nid = uid();
          doc.specialDays.push({ id: nid, day: to, title: src.title, closed: src.closed });
          for (const sh of doc.shifts.filter((x: any) => x.specialDayId === src.id))
            doc.shifts.push({ ...structuredClone(sh), id: uid(), specialDayId: nid });
        });
      }
      case 'add-area': {
        const kind = b.dataset.kind ?? 'indoor';
        const label = T(`env.${kind}`);
        const taken = new Set(doc.areas.map((a: any) => String(a.name).toLowerCase()));
        let name = label;
        for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${label} ${n}`;
        return edit(() =>
          doc.areas.push({ id: uid(), name, environment: kind, bookable: true, names: {}, descriptions: {} }),
        );
      }
      case 'del-area': {
        const a = doc.areas[i];
        if (!a || !confirm(T('c.a.delete_confirm', { name: a.name }))) return;
        return edit(() => {
          for (const t of doc.tables) if (t.diningAreaId === a.id) t.diningAreaId = null;
          doc.areas.splice(i, 1);
        });
      }
      case 'goto-layout':
        section = 'layout';
        return render();
      case 'photo-pick':
        (root.querySelector('#photo-file') as HTMLInputElement | null)?.click();
        return;
      case 'photo-del':
      case 'photo-left':
      case 'photo-right':
        return edit(() => {
          const g = ((getPath('branding.profile.gallery') as string[] | undefined) ?? []).slice();
          const j = act === 'photo-del' ? -1 : act === 'photo-left' ? i - 1 : i + 1;
          if (act === 'photo-del') g.splice(i, 1);
          else if (j >= 0 && j < g.length) [g[i], g[j]] = [g[j], g[i]];
          setPath('branding.profile.gallery', g);
        });
      case 'logo-pick':
        (root.querySelector('#logo-file') as HTMLInputElement | null)?.click();
        return;
      case 'logo-remove':
        return edit(() => {
          setPath('branding.logoAssetId', null);
          setPath('branding.logoUrl', null);
        });
      case 'goto-branding':
        section = 'branding';
        return render();
      case 'copy':
        try {
          await navigator.clipboard.writeText(b.dataset.text ?? '');
          opts.toast(T('c.w.embed_copied'));
        } catch {
          /* clipboard blocked: the text is selectable in the box */
        }
        return;
      case 'nav':
        section = b.dataset.s as Section;
        if (section === 'experiences') experiences = null;
        return render();
      case 'start':
        return startDraft(false);
      case 'discard':
        return discard();
      case 'publish':
        return publish();
      case 'reset-wording':
        return edit(() => {
          if (doc.emailTemplates?.[emailKind]) delete doc.emailTemplates[emailKind][emailLocale];
        });
      case 'restore':
        if (!confirm(T('c.restore_confirm', { v: b.dataset.v! }))) return;
        try {
          draft = await api('POST', `${base}/versions/${b.dataset.v}/restore`, {});
          doc = structuredClone(draft.doc);
          state.draft = draft;
          section = 'shifts';
          render();
        } catch (e) {
          err(e);
        }
        return;
      case 'add-shift':
        return edit(() =>
          doc.shifts.push({
            id: uid(), name: T('c.s.new'), color: '#2b7a9b', days: [1, 2, 3, 4, 5, 6, 7], first: '17:00', last: '21:00',
            maxOnlineParty: null, specialDayId: null, floorPlanIds: doc.floorPlans.map((f: any) => f.id),
            tablePolicy: [], flow: null, turnRules: [{ min: 1, max: 99, minutes: doc.settings.defaultTurnMinutes }],
          }),
        );
      case 'del-shift':
        return edit(() => doc.shifts.splice(i, 1));
      case 'add-turn':
        return edit(() => {
          const rules = doc.shifts[i].turnRules;
          const last = rules[rules.length - 1];
          const from = last ? last.max + 1 : 1;
          rules.push({ min: from, max: from + 1, minutes: doc.settings.defaultTurnMinutes });
        });
      case 'del-turn':
        return edit(() => doc.shifts[i].turnRules.splice(Number(b.dataset.k), 1));
      case 'add-table':
        return edit(() =>
          doc.tables.push({
            id: uid(), label: String(doc.tables.length + 1), floorPlanId: doc.floorPlans[0].id, diningAreaId: null,
            minSeats: 1, maxSeats: 4, category: 'regular', shape: 'rect', x: 24, y: 24, w: 1, h: 1, rotation: 0,
          }),
        );
      case 'del-table':
        return edit(() => {
          const id = doc.tables[i].id;
          doc.tables.splice(i, 1);
          for (const c of doc.combos) c.tableIds = c.tableIds.filter((x: string) => x !== id);
          doc.combos = doc.combos.filter((c: any) => c.tableIds.length >= 2);
          for (const s of doc.shifts) s.tablePolicy = s.tablePolicy.filter((p: any) => p.tableId !== id);
        });
      case 'add-combo':
        return edit(() => doc.combos.push({ id: uid(), minParty: 5, maxParty: 6, tableIds: [] }));
      case 'del-combo':
        return edit(() => doc.combos.splice(i, 1));
      case 'add-policy':
        return edit(() =>
          doc.policies.push({
            id: uid(), name: T('c.p.new'), kind: 'card_hold', minParty: 6, maxParty: null, cancelCutoffHours: 24,
            amountType: 'per_guest', amountMinor: 2500, chargeOn: 'both', message: null, messages: {},
          }),
        );
      case 'del-policy':
        return edit(() => doc.policies.splice(i, 1));
      case 'add-day':
        return edit(() => doc.specialDays.push({ id: uid(), day: new Date().toISOString().slice(0, 10), title: null, closed: true }));
      case 'del-day':
        return edit(() => {
          const id = doc.specialDays[i].id;
          doc.specialDays.splice(i, 1);
          for (const s of doc.shifts) if (s.specialDayId === id) s.specialDayId = null;
        });
    }
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') (root.querySelector('[data-do="close"]') as HTMLElement | null)?.click();
  });

  render();
  refresh().catch((e) => {
    err(e);
    root.remove();
    document.body.classList.remove('cfg-open');
    opts.onClose();
  });
}
